import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { AuthLayout } from '../../../../components/AuthLayout';
import { AuthFormHeader } from '../../../../components/AuthFormHeader';
import { Button } from '../../../../components/Button';
import { InputText } from '../../../../components/InputText';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import { getValidationErrors } from '../../../../utils/getValidationErrors';

import { Form } from './styles';

interface formProps {
  new_password: string;
  confirmation_new_password: string;
}

interface locationState {
  code?: string;
}

const ForgotPasswordStep3: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { addToast } = useToast();

  const code = (state as locationState)?.code;

  useEffect(() => {
    if (!code) {
      navigate('/forgot-password', { replace: true });
    }
  }, [code, navigate]);

  const handleSubmit = useCallback(
    async (data: formProps) => {
      try {
        formRef.current?.setErrors({});

        const schema = yup.object().shape({
          new_password: yup
            .string()
            .min(8, 'A senha deve ter no mínimo 8 caracteres')
            .required('Campo obrigatório.'),
          confirmation_new_password: yup
            .string()
            .min(8, 'A senha deve ter no mínimo 8 caracteres')
            .oneOf([yup.ref('new_password'), null], 'Senhas não coincidem.')
            .required('Campo obrigatório.'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.put(
          'users/password/change-forgot',
          {
            new_password: data.new_password,
            confirmation_new_password: data.confirmation_new_password,
          },
          {
            headers: {
              code: (code || '').toUpperCase(),
            },
          },
        );

        addToast({
          type: 'success',
          title: 'Senha alterada com sucesso',
          description: 'Faça o login para ter acesso a nossa plataforma.',
        });

        navigate('/sign-in');
      } catch (err: any) {
        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na requisição',
          description:
            'Houve um erro na requisição realizada. Tente novamente mais tarde.',
        });
      }
    },
    [addToast, code, navigate],
  );

  return (
    <AuthLayout
      eyebrow="Protocolo de segurança"
      title={
        <>
          REDEFINA <span>SEU ACESSO</span>
        </>
      }
      subtitle="Escolha uma nova senha para proteger sua conta e voltar a gerenciar sua academia."
    >
      <AuthFormHeader
        badge="Sessão ativa"
        title="Crie uma nova senha"
        subtitle="Use uma senha com pelo menos 8 caracteres para manter sua conta protegida."
      />

      <Form ref={formRef} onSubmit={handleSubmit}>
        <InputText name="new_password" label="Nova senha" type="password" />
        <InputText
          name="confirmation_new_password"
          label="Confirme a nova senha"
          type="password"
        />

        <Button name="submit" value="Atualizar senha" />
      </Form>
    </AuthLayout>
  );
};

export { ForgotPasswordStep3 };
